import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import socket from '../utils/socket';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

// WhatsApp contact for the widget's "Chat on WhatsApp" option.
// International format (no '+'/leading zero): 03326149157 -> 923326149157.
const WHATSAPP_NUMBER = '923326149157';
const WHATSAPP_GREETING = 'Hello';

const COUNTRY_CODES = [
  { code: '+1',   label: '🇺🇸 +1 (US/CA)' },
  { code: '+44',  label: '🇬🇧 +44 (UK)' },
  { code: '+91',  label: '🇮🇳 +91 (IN)' },
  { code: '+92',  label: '🇵🇰 +92 (PK)' },
  { code: '+971', label: '🇦🇪 +971 (UAE)' },
  { code: '+966', label: '🇸🇦 +966 (KSA)' },
  { code: '+61',  label: '🇦🇺 +61 (AU)' },
  { code: '+81',  label: '🇯🇵 +81 (JP)' },
  { code: '+86',  label: '🇨🇳 +86 (CN)' },
  { code: '+33',  label: '🇫🇷 +33 (FR)' },
  { code: '+49',  label: '🇩🇪 +49 (DE)' },
  { code: '+34',  label: '🇪🇸 +34 (ES)' },
  { code: '+39',  label: '🇮🇹 +39 (IT)' },
  { code: '+7',   label: '🇷🇺 +7 (RU)' },
  { code: '+55',  label: '🇧🇷 +55 (BR)' },
  { code: '+52',  label: '🇲🇽 +52 (MX)' },
  { code: '+27',  label: '🇿🇦 +27 (ZA)' },
  { code: '+82',  label: '🇰🇷 +82 (KR)' },
  { code: '+65',  label: '🇸🇬 +65 (SG)' },
  { code: '+62',  label: '🇮🇩 +62 (ID)' },
  { code: '+60',  label: '🇲🇾 +60 (MY)' },
  { code: '+66',  label: '🇹🇭 +66 (TH)' },
  { code: '+20',  label: '🇪🇬 +20 (EG)' },
  { code: '+90',  label: '🇹🇷 +90 (TR)' },
  { code: '+964', label: '🇮🇶 +964 (IQ)' },
  { code: '+98',  label: '🇮🇷 +98 (IR)' },
  { code: '+972', label: '🇮🇱 +972 (IL)' },
  { code: '+31',  label: '🇳🇱 +31 (NL)' },
  { code: '+46',  label: '🇸🇪 +46 (SE)' },
  { code: '+48',  label: '🇵🇱 +48 (PL)' }
];

const storageKey = (workspaceUuid) => `omnitrace_visitor_${workspaceUuid}`;

const WebsiteChat = () => {
  const { workspaceUuid, visitorId: urlVisitorId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const embedMode = searchParams.get('embed') === '1' || location.pathname.endsWith('/embed');
  // Anonymous mode (Shopify storefront widget & any embed that should skip the
  // sign-in gate). Auto-creates a throwaway `web-<uuid>` visitor on first load.
  const anonMode = searchParams.get('anon') === '1';
  const anonSuffix = anonMode ? '?anon=1' : '';

  const chatPathFor = (vId) => {
    const base = `/chat/${workspaceUuid}/users/${vId}`;
    return (embedMode ? `${base}/embed` : base) + anonSuffix;
  };
  const signinPath = (embedMode ? `/chat/${workspaceUuid}/embed` : `/chat/${workspaceUuid}`) + anonSuffix;

  const [visitor, setVisitor] = useState(() => {
    try { return JSON.parse(localStorage.getItem(storageKey(workspaceUuid)) || 'null'); } catch { return null; }
  });
  const [bootstrapping, setBootstrapping] = useState(!!urlVisitorId); // true while we validate the URL visitorId

  // Bootstrap: reconcile URL visitorId ↔ localStorage ↔ backend.
  useEffect(() => {
    const stored = (() => { try { return JSON.parse(localStorage.getItem(storageKey(workspaceUuid)) || 'null'); } catch { return null; } })();

    // Case 1: URL has visitorId → validate it against backend.
    if (urlVisitorId) {
      (async () => {
        try {
          const res = await axios.get(`${API_URL}/api/integrations/website/history/${urlVisitorId}?workspaceUuid=${workspaceUuid}`);
          const v = res.data.visitor;
          const full = { visitorId: v.visitorId, contactId: v.contactId, method: v.method, name: v.name };
          localStorage.setItem(storageKey(workspaceUuid), JSON.stringify(full));
          setVisitor(full);
        } catch {
          // invalid / unknown visitorId — drop to sign-in
          localStorage.removeItem(storageKey(workspaceUuid));
          setVisitor(null);
          navigate(signinPath, { replace: true });
        } finally {
          setBootstrapping(false);
        }
      })();
      return;
    }

    // Case 2: no URL visitorId but localStorage has one → redirect to URL form.
    if (stored?.visitorId) {
      navigate(chatPathFor(stored.visitorId), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlVisitorId, workspaceUuid]);

  // Anonymous widget: the customer first picks a channel (WhatsApp or AI).
  // Only "Chat with AI" mints a throwaway visitor and opens the in-app chat.
  const [anonCreating, setAnonCreating] = useState(false);

  const startAiChat = async () => {
    if (anonCreating) return;
    setAnonCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/integrations/website/signup`, { workspaceUuid, method: 'anonymous' });
      const v = res.data;
      localStorage.setItem(storageKey(workspaceUuid), JSON.stringify(v));
      setVisitor(v);
      navigate(chatPathFor(v.visitorId), { replace: true });
    } catch (err) {
      console.error('Anonymous sign-in failed:', err);
      alert('Could not start chat. Please try again.');
    } finally {
      setAnonCreating(false);
    }
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_GREETING)}`, '_blank');
  };

  // Sign-in form state
  const [method, setMethod] = useState('email'); // 'email' | 'phone'
  const [email, setEmail] = useState('');
  const [countryCode, setCountryCode] = useState('+92');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [signingIn, setSigningIn] = useState(false);
  const [signinError, setSigninError] = useState('');

  // Chat state
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  // Load history + join socket room when visitor changes
  useEffect(() => {
    if (!visitor?.visitorId) return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/integrations/website/history/${visitor.visitorId}?workspaceUuid=${workspaceUuid}`);
        setMessages(res.data.messages || []);
        setTimeout(scrollToBottom, 100);
      } catch (err) {
        console.error('Failed to load history:', err);
      }
    })();

    socket.emit('joinVisitor', visitor.visitorId);
    const handler = (msg) => {
      setMessages(prev => {
        // dedup by real id
        if (prev.some(m => m._id === msg._id)) return prev;
        // also dedup against an optimistic pending copy with the same text + direction
        const tmpIdx = prev.findIndex(m => m.pending && m.direction === msg.direction && m.text?.body === msg.text?.body);
        if (tmpIdx !== -1) {
          const next = prev.slice();
          next[tmpIdx] = msg;
          return next;
        }
        return [...prev, msg];
      });
      setTimeout(scrollToBottom, 50);
    };
    socket.on('visitorMessage', handler);
    return () => socket.off('visitorMessage', handler);
  }, [visitor, workspaceUuid]);

  const handleSignIn = async (e) => {
    e.preventDefault();
    setSigningIn(true);
    setSigninError('');
    try {
      const payload = { workspaceUuid, method, name: name.trim() || undefined };
      if (method === 'email') payload.email = email.trim();
      else { payload.countryCode = countryCode; payload.phone = phone.trim(); }

      const res = await axios.post(`${API_URL}/api/integrations/website/signup`, payload);
      const v = res.data;
      localStorage.setItem(storageKey(workspaceUuid), JSON.stringify(v));
      setVisitor(v);
      navigate(chatPathFor(v.visitorId), { replace: true });
    } catch (err) {
      setSigninError(err.response?.data?.message || 'Failed to sign in');
    } finally {
      setSigningIn(false);
    }
  };

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text || sending) return;
    setInputText('');
    setSending(true);
    const optimistic = {
      _id: 'tmp_' + Date.now(),
      from: visitor.contactId,
      to: 'system',
      type: 'text',
      text: { body: text },
      direction: 'incoming',
      platform: 'website',
      timestamp: new Date().toISOString(),
      pending: true
    };
    setMessages(prev => [...prev, optimistic]);
    setTimeout(scrollToBottom, 30);

    try {
      const res = await axios.post(`${API_URL}/api/integrations/website/send`, {
        workspaceUuid, visitorId: visitor.visitorId, text
      });
      setMessages(prev => prev.map(m => m._id === optimistic._id ? res.data : m));
    } catch (err) {
      setMessages(prev => prev.filter(m => m._id !== optimistic._id));
      alert('Failed to send: ' + (err.response?.data?.message || err.message));
    } finally {
      setSending(false);
    }
  };

  const handleSignOut = () => {
    localStorage.removeItem(storageKey(workspaceUuid));
    setVisitor(null);
    setMessages([]);
    setEmail(''); setPhone(''); setName('');
    navigate(signinPath, { replace: true });
  };

  // ===== Styles =====
  const page = embedMode
    ? { height: '100vh', width: '100%', background: 'white', display: 'flex', flexDirection: 'column', fontFamily: '"Outfit", system-ui, sans-serif' }
    : { minHeight: '100vh', width: '100%', background: 'linear-gradient(135deg, #eef2ff 0%, #dbeafe 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', fontFamily: '"Outfit", system-ui, sans-serif', boxSizing: 'border-box' };
  const card = embedMode
    ? { flex: 1, display: 'flex', flexDirection: 'column', background: 'white', overflow: 'hidden' }
    : { width: '100%', maxWidth: '480px', height: '720px', maxHeight: '90vh', background: 'white', borderRadius: '24px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', overflow: 'hidden' };

  const brandBar = (
    <div style={{ padding: '14px 20px', background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: '15px', fontWeight: 800, letterSpacing: '-0.01em' }}>Chat with us</div>
        <div style={{ fontSize: '11px', opacity: 0.7, marginTop: '2px' }}>We usually reply within a few minutes</div>
      </div>
      {visitor && (
        <button onClick={handleSignOut} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'white', padding: '6px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: 700, cursor: 'pointer' }}>End chat</button>
      )}
    </div>
  );

  const footer = (
    <div style={{ padding: '8px 16px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', fontSize: '10px', textAlign: 'center', color: '#64748b', fontWeight: 600, letterSpacing: '0.02em' }}>
      powered by <a href="https://vetobase.com" target="_blank" rel="noreferrer" style={{ color: '#3b82f6', textDecoration: 'none', fontWeight: 700 }}>vetobase.com</a>
    </div>
  );

  // ===== Loading view (validating URL visitorId or creating anon visitor) =====
  if ((bootstrapping || anonCreating) && !visitor) {
    return (
      <div style={page}>
        <div style={card}>
          {brandBar}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: '13px' }}>
            Loading…
          </div>
          {footer}
        </div>
      </div>
    );
  }

  // ===== Channel choice (anonymous widget) =====
  if (anonMode && !visitor) {
    return (
      <div style={page}>
        <div style={card}>
          {brandBar}
          <div style={{ flex: 1, overflowY: 'auto', padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: '14px', justifyContent: 'center' }}>
            <div style={{ textAlign: 'center', marginBottom: '4px' }}>
              <div style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>How would you like to chat?</div>
              <div style={{ fontSize: '13px', color: '#64748b' }}>Choose an option to get started.</div>
            </div>

            <button onClick={openWhatsApp} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '15px', borderRadius: '12px', border: 'none', background: '#25D366', color: 'white', fontWeight: 800, fontSize: '15px', cursor: 'pointer' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.522 5.276l-.999 3.648 3.967-1.023zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413z"/></svg>
              Chat on WhatsApp
            </button>

            <button onClick={startAiChat} disabled={anonCreating} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '15px', borderRadius: '12px', border: 'none', background: '#0f172a', color: 'white', fontWeight: 800, fontSize: '15px', cursor: 'pointer', opacity: anonCreating ? 0.6 : 1 }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              {anonCreating ? 'Starting…' : 'Chat with AI'}
            </button>
          </div>
          {footer}
        </div>
      </div>
    );
  }

  // ===== Sign-in view =====
  if (!visitor) {
    return (
      <div style={page}>
        <div style={card}>
          {brandBar}
          <div style={{ flex: 1, overflowY: 'auto', padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div>
              <div style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', marginBottom: '6px' }}>Let's get started</div>
              <div style={{ fontSize: '13px', color: '#64748b' }}>Sign in with email or phone to chat with our team.</div>
            </div>

            <div style={{ display: 'flex', gap: '8px', background: '#f1f5f9', padding: '4px', borderRadius: '10px' }}>
              <button onClick={() => setMethod('email')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: method === 'email' ? 'white' : 'transparent', color: method === 'email' ? '#0f172a' : '#64748b', fontWeight: 700, fontSize: '13px', cursor: 'pointer', boxShadow: method === 'email' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none' }}>Email</button>
              <button onClick={() => setMethod('phone')} style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: method === 'phone' ? 'white' : 'transparent', color: method === 'phone' ? '#0f172a' : '#64748b', fontWeight: 700, fontSize: '13px', cursor: 'pointer', boxShadow: method === 'phone' ? '0 1px 2px rgba(0,0,0,0.05)' : 'none' }}>Phone</button>
            </div>

            <form onSubmit={handleSignIn} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '6px' }}>Your name (optional)</label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Sara" style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
              </div>

              {method === 'email' ? (
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '6px' }}>Email</label>
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" style={{ width: '100%', padding: '12px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ) : (
                <div>
                  <label style={{ fontSize: '11px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '6px' }}>Phone</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <select value={countryCode} onChange={e => setCountryCode(e.target.value)} style={{ width: '150px', padding: '12px 8px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '13px', outline: 'none', background: 'white' }}>
                      {COUNTRY_CODES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </select>
                    <input type="tel" required value={phone} onChange={e => setPhone(e.target.value.replace(/[^\d]/g, ''))} placeholder="3001234567" style={{ flex: 1, padding: '12px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none', boxSizing: 'border-box' }} />
                  </div>
                </div>
              )}

              {signinError && <div style={{ fontSize: '12px', color: '#dc2626', background: '#fee2e2', padding: '8px 12px', borderRadius: '8px' }}>{signinError}</div>}

              <button type="submit" disabled={signingIn} style={{ padding: '14px', borderRadius: '10px', border: 'none', background: '#0f172a', color: 'white', fontWeight: 800, fontSize: '14px', cursor: 'pointer', marginTop: '4px', opacity: signingIn ? 0.6 : 1 }}>
                {signingIn ? 'Signing in…' : 'Start chatting'}
              </button>
            </form>
          </div>
          {footer}
        </div>
      </div>
    );
  }

  // ===== Chat view =====
  return (
    <div style={page}>
      <div style={card}>
        {brandBar}
        <div style={{ padding: '8px 20px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontSize: '11px', color: '#64748b' }}>
          Signed in as <strong style={{ color: '#0f172a' }}>{visitor.name || visitor.contactId}</strong>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px', background: '#f8fafc', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '13px', padding: '40px 20px' }}>
              Send a message to start the conversation.
            </div>
          )}
          {messages.map(msg => {
            const isMe = msg.direction === 'incoming'; // visitor's own messages
            return (
              <div key={msg._id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '75%',
                  padding: '10px 14px',
                  borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: isMe ? '#0f172a' : 'white',
                  color: isMe ? 'white' : '#0f172a',
                  fontSize: '14px',
                  lineHeight: 1.45,
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  border: isMe ? 'none' : '1px solid #e2e8f0',
                  opacity: msg.pending ? 0.6 : 1
                }}>
                  {msg.text?.body}
                  <div style={{ fontSize: '10px', opacity: 0.6, marginTop: '4px', textAlign: isMe ? 'right' : 'left' }}>
                    {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    {!isMe && msg.sentBy && ` • ${msg.sentBy}`}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={messagesEndRef} />
        </div>
        <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} style={{ display: 'flex', gap: '8px', padding: '14px', borderTop: '1px solid #e2e8f0', background: 'white' }}>
          <input type="text" value={inputText} onChange={e => setInputText(e.target.value)} placeholder="Type your message…" style={{ flex: 1, padding: '12px 14px', borderRadius: '10px', border: '1px solid #e2e8f0', fontSize: '14px', outline: 'none' }} />
          <button type="submit" disabled={sending || !inputText.trim()} style={{ padding: '0 18px', borderRadius: '10px', border: 'none', background: '#0f172a', color: 'white', fontWeight: 700, cursor: 'pointer', opacity: (sending || !inputText.trim()) ? 0.5 : 1 }}>
            Send
          </button>
        </form>
        {footer}
      </div>
    </div>
  );
};

export default WebsiteChat;
